/**
 * Template Service
 * Handles all HTTP requests for template management
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  Template,
  CreateTemplateRequest,
  CreateTemplateResponse,
  UpdateTemplateRequest,
  CloneTemplateRequest,
  CloneTemplateResponse,
  TemplateFilters,
  PaginatedTemplateResponse,
  TemplateTestResponse,
  DeleteTemplateResponse,
  EnableTemplateRequest,
  DisableTemplateRequest,
  EnabledTemplatesResponse,
  TemplateSelectionResponse
} from '../models/template.models';

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  private http = inject(HttpClient);
  private readonly API_URL = `${environment.apiUrl}/templates`;

  /**
   * Create a new template
   */
  createTemplate(request: CreateTemplateRequest): Observable<CreateTemplateResponse> {
    return this.http.post<CreateTemplateResponse>(this.API_URL, request);
  }

  /**
   * Get list of templates with pagination and filters
   */
  getTemplates(filters: TemplateFilters = {}): Observable<PaginatedTemplateResponse> {
    let params = new HttpParams();
    
    if (filters.page) {
      params = params.set('page', filters.page.toString());
    }
    if (filters.page_size) {
      params = params.set('page_size', filters.page_size.toString());
    }
    if (filters.document_type) {
      params = params.set('document_type', filters.document_type);
    }
    if (filters.is_active !== undefined) {
      params = params.set('is_active', filters.is_active.toString());
    }
    if (filters.include_public !== undefined) {
      params = params.set('include_public', filters.include_public.toString());
    }

    return this.http.get<PaginatedTemplateResponse>(this.API_URL, { params });
  }

  /**
   * Get list of public templates
   */
  getPublicTemplates(filters: TemplateFilters = {}): Observable<PaginatedTemplateResponse> {
    let params = new HttpParams();
    
    if (filters.page) {
      params = params.set('page', filters.page.toString());
    }
    if (filters.page_size) {
      params = params.set('page_size', filters.page_size.toString());
    }
    if (filters.document_type) {
      params = params.set('document_type', filters.document_type);
    }

    return this.http.get<PaginatedTemplateResponse>(`${this.API_URL}/public`, { params });
  }

  /**
   * Get a single template by ID
   */
  getTemplate(id: string): Observable<Template> {
    return this.http.get<Template>(`${this.API_URL}/${id}`);
  }

  getAiProviders(): Observable<{ providers: Array<{ id: string; label: string; available?: boolean }> }> {
    return this.http.get<{ providers: Array<{ id: string; label: string }> }>(
      `${this.API_URL}/ai-providers`
    );
  }

  /**
   * Update an existing template
   */
  updateTemplate(id: string, request: UpdateTemplateRequest): Observable<Template> {
    return this.http.put<Template>(`${this.API_URL}/${id}`, request);
  }

  /**
   * Delete a template (soft delete)
   */
  deleteTemplate(id: string, hard_delete: boolean = false): Observable<DeleteTemplateResponse> {
    let params = new HttpParams();
    if (hard_delete) {
      params = params.set('hard_delete', 'true');
    }
    return this.http.delete<DeleteTemplateResponse>(`${this.API_URL}/${id}`, { params });
  }

  /**
   * Clone an existing template
   */
  cloneTemplate(id: string, request: CloneTemplateRequest): Observable<CloneTemplateResponse> {
    return this.http.post<CloneTemplateResponse>(`${this.API_URL}/${id}/clone`, request);
  }

  /**
   * Test a template with a document
   */
  testTemplate(id: string, file: File): Observable<TemplateTestResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.http.post<TemplateTestResponse>(`${this.API_URL}/${id}/test`, formData);
  }

  /**
   * Get template statistics
   */
  getTemplateStats(id: string): Observable<{
    usage_count: number;
    avg_confidence_score: number;
    success_rate: number;
    last_used_at?: string;
  }> {
    return this.http.get<{
      usage_count: number;
      avg_confidence_score: number;
      success_rate: number;
      last_used_at?: string;
    }>(`${this.API_URL}/${id}/stats`);
  }

  /**
   * Preview detection cost before AI field detection
   * Shows page count and cost to user before proceeding
   */
  previewDetectionCost(file: File): Observable<DetectionCostPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<DetectionCostPreviewResponse>(`${this.API_URL}/preview-detection-cost`, formData);
  }

  /**
   * Detect fields from a document using AI
   * Used during template creation to suggest fields
   * Note: This deducts credits based on page count
   */
  detectFields(
    file: File,
    documentType?: string,
    preferredProvider?: string
  ): Observable<DetectFieldsResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (documentType) {
      formData.append('document_type', documentType);
    }
    if (preferredProvider) {
      formData.append('preferred_provider', preferredProvider);
    }

    return this.http.post<DetectFieldsResponse>(`${this.API_URL}/detect-fields`, formData);
  }

  /**
   * Preview extraction using template definition before saving
   * Used to test template configuration in the Create Template wizard
   */
  previewExtraction(
    file: File,
    templateDefinition: TemplateDefinition,
    preferredProvider?: string
  ): Observable<PreviewExtractionResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_json', JSON.stringify(templateDefinition));
    if (preferredProvider) {
      formData.append('preferred_provider', preferredProvider);
    }

    return this.http.post<PreviewExtractionResponse>(`${this.API_URL}/preview-extraction`, formData);
  }

  // ============================================================
  // Template Selection Methods (for API Usage)
  // ============================================================

  /**
   * Check whether a template name is available within the organization.
   */
  checkTemplateName(name: string, excludeId?: string): Observable<TemplateNameCheckResponse> {
    let params = new HttpParams().set('name', name.trim());
    if (excludeId) {
      params = params.set('exclude_id', excludeId);
    }
    return this.http.get<TemplateNameCheckResponse>(`${this.API_URL}/check-name`, { params });
  }

  /**
   * Get list of enabled templates for API usage
   */
  getEnabledTemplates(): Observable<EnabledTemplatesResponse> {
    return this.http.get<EnabledTemplatesResponse>(`${this.API_URL}/enabled`);
  }

  /**
   * Enable templates for API usage
   */
  enableTemplates(request: EnableTemplateRequest): Observable<TemplateSelectionResponse> {
    return this.http.post<TemplateSelectionResponse>(`${this.API_URL}/enable`, request);
  }

  /**
   * Disable templates from API usage
   */
  disableTemplates(request: DisableTemplateRequest): Observable<TemplateSelectionResponse> {
    return this.http.post<TemplateSelectionResponse>(`${this.API_URL}/disable`, request);
  }

  getTemplateDraft(): Observable<TemplateDraftResponse> {
    return this.http.get<TemplateDraftResponse>(`${this.API_URL}/draft`);
  }

  saveTemplateDraft(draftData: Record<string, unknown>): Observable<TemplateDraftResponse> {
    return this.http.put<TemplateDraftResponse>(`${this.API_URL}/draft`, { draft_data: draftData });
  }

  deleteTemplateDraft(): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/draft`);
  }
}

export interface TemplateDraftResponse {
  draft_data: Record<string, unknown>;
  updated_at?: string | null;
}

/**
 * Response from template name availability check
 */
export interface TemplateNameCheckResponse {
  available: boolean;
}

/**
 * Response from detection cost preview
 */
export interface DetectionCostPreviewResponse {
  page_count: number;
  cost_usd: number;
  credits_required: number;
  cost_per_page: number;
  pricing_tier: string;
  current_credits: number;
  has_sufficient_credits: boolean;
  credits_needed: number;
}

/**
 * Response from AI field detection
 */
export interface DetectFieldsResponse {
  success: boolean;
  detected_fields: DetectedFieldInfo[];
  document_type_suggestion?: string;
  document_language?: string;
  processing_time_ms: number;
  provider_used: string;
  error_message?: string;
  // Page counting and credit information
  page_count?: number;
  credits_deducted?: number;
  credits_remaining?: number;
}

/**
 * Field detected by AI from document analysis
 */
export interface DetectedFieldInfo {
  name: string;
  label: string;
  type: string;
  confidence: number;
  required: boolean;
  sample_value?: string;
  extraction_hint?: string;
}

/**
 * Template definition for preview extraction
 */
export interface TemplateDefinition {
  name: string;
  document_type?: string;
  fields: TemplateFieldDefinition[];
  instructions?: string;
}

/**
 * Field definition within a template
 */
export interface TemplateFieldDefinition {
  name: string;
  label: string;
  type: string;
  required: boolean;
  extraction_hints?: string;
  default_value?: string;
  validation_rules?: Record<string, any>;
}

/**
 * Single field extraction result from preview
 */
export interface PreviewFieldExtraction {
  field_name: string;
  value: any;
  confidence: number;
  matched: boolean;
}

/**
 * Response from preview extraction
 */
export interface PreviewExtractionResponse {
  success: boolean;
  extracted_data: Record<string, any>;
  field_extractions: PreviewFieldExtraction[];
  overall_confidence: number;
  validation_results: Record<string, { valid: boolean; message: string }>;
  processing_time_ms: number;
  provider_used: string;
  error_message?: string;
}

